import type { ComponentType } from "react";
import {
  LayoutDashboard, Users, ImageIcon, Images, Settings, Palette,
  Clapperboard, MessageSquareText, BarChart3, Printer,
} from "lucide-react";
import type { RoutePath } from "@wedding/shared";
import { paths } from "./paths";
import { MyWeddingsPage } from "../pages/my-weddings";
import { ProfilePage } from "../pages/profile";
import { PackagesPage } from "../pages/packages";
import { OrganizerSupportPage, OrganizerSupportDetailPage } from "../pages/support";
import { DashboardPage } from "../pages/dashboard";
import { GuestsPage } from "../pages/guests";
import { ModerationPage } from "../pages/moderation";
import { GalleryPage } from "../pages/gallery";
import { SmsCampaignsPage } from "../pages/sms-campaigns";
import { AnalyticsPage } from "../pages/analytics";
import { PrintPage } from "../pages/print";
import { RejiPage } from "../pages/reji";
import { ThemesPage } from "../pages/themes";
import { SettingsPage } from "../pages/settings";

type IconType = ComponentType<{ className?: string }>;

/**
 * Düğün dışı (global) organizatör rotası. Bu seviyede sidebar YOK — bu yüzden
 * `labelKey`/`icon` da yok; nav sadece düğün içinde var.
 */
export interface OrganizerGlobalRoute {
  /** react-router yolu; `undefined` = index rotası. */
  routePath?: string;
  element: ComponentType;
}

/** `/:slug` altındaki düğün-içi rota. */
export interface OrganizerWeddingRoute {
  /** `/:slug` altındaki göreli segment; `undefined` = index rotası. */
  routePath?: string;
  to: (slug: string) => RoutePath;
  /** i18n anahtarı — `scripts/validate-i18n-keys.js` bunu da denetler. */
  labelKey: string;
  icon: IconType;
  end?: boolean;
  element: ComponentType;
  /**
   * `true` ise rota düğün chrome'unun İÇİNDEKİ `<Routes>`'a DEĞİL, üst seviyeye
   * mount edilir (tam ekran; kendi provider'ı ve feature gate'i var).
   * Nav'da yine görünür — nav listesi ile route listesi bilerek 1:1 değil.
   */
  outsideLayout?: boolean;
  /** Nav'da bekleyen-moderasyon rozetini taşır. */
  showsPendingBadge?: boolean;
}

export const ORGANIZER_GLOBAL_ROUTES: OrganizerGlobalRoute[] = [
  { element: MyWeddingsPage },
  { routePath: paths.profile, element: ProfilePage },
  { routePath: paths.packages, element: PackagesPage },
  { routePath: paths.support, element: OrganizerSupportPage },
  { routePath: "/support/:id", element: OrganizerSupportDetailPage },
];

export const ORGANIZER_WEDDING_ROUTES: OrganizerWeddingRoute[] = [
  { to: paths.wedding.dashboard, labelKey: "nav.dashboard", icon: LayoutDashboard, end: true, element: DashboardPage },
  { routePath: "guests", to: paths.wedding.guests, labelKey: "nav.guests", icon: Users, element: GuestsPage },
  { routePath: "moderation", to: paths.wedding.moderation, labelKey: "nav.moderation", icon: ImageIcon, element: ModerationPage, showsPendingBadge: true },
  { routePath: "gallery", to: paths.wedding.gallery, labelKey: "nav.gallery", icon: Images, element: GalleryPage },
  { routePath: "sms", to: paths.wedding.sms, labelKey: "nav.sms", icon: MessageSquareText, element: SmsCampaignsPage },
  { routePath: "analytics", to: paths.wedding.analytics, labelKey: "nav.analytics", icon: BarChart3, element: AnalyticsPage },
  { routePath: "print", to: paths.wedding.print, labelKey: "nav.print", icon: Printer, element: PrintPage },
  { routePath: "control-room", to: paths.wedding.reji, labelKey: "nav.reji", icon: Clapperboard, element: RejiPage, outsideLayout: true },
  { routePath: "themes", to: paths.wedding.themes, labelKey: "nav.themes", icon: Palette, element: ThemesPage },
  { routePath: "settings", to: paths.wedding.settings, labelKey: "nav.settings", icon: Settings, element: SettingsPage },
];

/** Düğün chrome'unun içinde mount edilecek rotalar (reji hariç). */
export const ORGANIZER_WEDDING_LAYOUT_ROUTES = ORGANIZER_WEDDING_ROUTES.filter(
  (r) => !r.outsideLayout
);
